// Small, consistent building blocks used on every page.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { STATUS, formatMoney, formatDate } from '../../lib/viz';

export function PageHeader({ title, subtitle, eyebrow, actions }: { title: string; subtitle?: string; eyebrow?: string; actions?: ReactNode }) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="display text-[30px] text-ink-900 sm:text-[34px]">{title}</h1>
        {subtitle && <p className="mt-1.5 max-w-2xl text-[13.5px] leading-relaxed text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = '', title, subtitle, actions }: { children: ReactNode; className?: string; title?: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className={`surface ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-5 py-3.5">
          <div>
            {title && <h3 className="text-[13px] font-semibold text-ink-900">{title}</h3>}
            {subtitle && <p className="text-[11.5px] text-ink-400">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={title || actions ? 'p-5' : ''}>{children}</div>
    </div>
  );
}

/** Status pill. Colour is drawn from the reserved status palette and always paired with the label. */
const STATUS_TONE: Record<string, keyof typeof STATUS> = {
  ACTIVE: 'good', PAID: 'good', PUBLISHED: 'good', CLOSED: 'good', VERIFIED: 'good', PROTECTED: 'good', VALID: 'good', OPERATIONAL: 'good', COMPLETED: 'good', APPROVED: 'good', CLEARED: 'good', OCCUPIED: 'good', CONVERTED: 'good', POSTED: 'good', OPEN: 'good',
  NEW: 'neutral', DRAFT: 'neutral', DUE: 'neutral', ISSUED: 'neutral', VACANT: 'neutral', BOOKED: 'neutral', SUBMITTED: 'neutral', PENDING: 'neutral', HELD: 'neutral', SCHEDULED: 'neutral',
  TRIAGED: 'warning', ASSIGNED: 'warning', IN_PROGRESS: 'warning', NOTICE: 'warning', NOTICE_GIVEN: 'warning', PART_PAID: 'warning', PARTIAL: 'warning', EXPIRING: 'warning', RESERVED: 'warning', REFERENCING: 'warning', CONTACTED: 'warning', VIEWING_BOOKED: 'warning', AWAITING_PARTS: 'warning', ON_HOLD: 'warning', UNDER_REPAIR: 'warning', AUTO_EXECUTED: 'warning',
  OVERDUE: 'critical', EXPIRED: 'critical', FAULTY: 'critical', REJECTED: 'critical', FAILED: 'critical', CANCELLED: 'critical', VOID: 'critical', LOST: 'critical', REMEDIAL_REQUIRED: 'critical', DISPUTED: 'critical',
  EMERGENCY: 'critical', URGENT: 'serious', ROUTINE: 'neutral', PLANNED: 'good',
};
export function Badge({ value, tone }: { value: string | null | undefined; tone?: keyof typeof STATUS }) {
  if (!value) return <span className="text-ink-300">—</span>;
  const t = tone ?? STATUS_TONE[value] ?? 'neutral';
  const color = STATUS[t];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-[3px] text-[11px] font-medium leading-none" style={{ borderColor: `${color}40`, color: t === 'neutral' ? '#585550' : color, background: t === 'neutral' ? '#f7f5f1' : `${color}12` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
    </span>
  );
}

export const Money = ({ minor, currency = 'GBP', className = '' }: { minor: number | null | undefined; currency?: string; className?: string }) =>
  <span className={`tabular-nums ${className}`}>{minor === null || minor === undefined ? '—' : formatMoney(minor, currency)}</span>;

export const DateText = ({ value }: { value: string | null | undefined }) => <span>{value ? formatDate(value) : '—'}</span>;

export function Button({ children, variant = 'primary', size = 'md', icon, className = '', ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'md'; icon?: string }) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-xl font-medium transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';
  const sizes = { sm: 'px-3 py-1.5 text-[12px]', md: 'px-4 py-2.5 text-[13px]' };
  const variants = {
    primary: 'bg-ink-900 text-white shadow-[0_1px_0_rgba(255,255,255,0.08)_inset] hover:bg-ink-800',
    accent: 'bg-bronze-500 text-white hover:bg-bronze-600',
    secondary: 'border border-ink-200 bg-white text-ink-800 hover:border-ink-300 hover:bg-ink-50',
    ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  };
  const I = icon ? (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[icon] : null;
  return <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...rest}>{I && <I className="h-4 w-4" />}{children}</button>;
}

const CONTROL = 'rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-[13px] text-ink-900 placeholder:text-ink-400 outline-none transition focus:border-bronze-400 focus:ring-4 focus:ring-bronze-100 disabled:bg-ink-50 disabled:text-ink-400';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${props.className ?? ''}`} />;
}
export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${CONTROL} pr-8 ${props.className ?? ''}`} />;
}
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${CONTROL} ${props.className ?? ''}`} />;
}
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="block"><span className="mb-1.5 block text-[12px] font-medium text-ink-600">{label}</span>{children}{hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-ink-400">{hint}</span>}</label>;
}

export function EmptyState({ icon = 'Inbox', title, body, action }: { icon?: string; title: string; body?: string; action?: ReactNode }) {
  const I = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[icon] ?? Icons.Inbox;
  return (
    <div className="rounded-2xl border border-dashed border-ink-200 bg-white/60 py-16 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-bronze-50"><I className="h-5 w-5 text-bronze-500" /></div>
      <p className="display mt-4 text-[22px] text-ink-900">{title}</p>
      {body && <p className="mx-auto mt-1.5 max-w-sm text-[13px] text-ink-500">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; size?: 'md' | 'lg' }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`fixed inset-x-4 top-[8vh] z-50 mx-auto max-h-[84vh] w-auto ${size === 'lg' ? 'max-w-3xl' : 'max-w-lg'} overflow-y-auto rounded-[20px] bg-white shadow-pop`}>
        <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
          <h3 className="display text-[22px] text-ink-900">{title}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-ink-400 transition hover:bg-ink-100 hover:text-ink-900"><Icons.X className="h-4 w-4" /></button>
        </div>
        <div className="p-6">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-6 py-4">{footer}</div>}
      </div>
    </>
  );
}

export function KeyValue({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
      {items.map((it) => (<div key={it.label}><dt className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{it.label}</dt><dd className="mt-1 text-[13.5px] text-ink-800">{it.value ?? '—'}</dd></div>))}
    </dl>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return <Link to={to} className="mb-4 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400 transition hover:text-ink-900"><Icons.ArrowLeft className="h-3.5 w-3.5" />{label}</Link>;
}
